import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Plus, X, RefreshCcw, Trash2 } from "lucide-react";

/**
 * Phase 7A.3 — Explicit Inventory Conversions.
 *   /admin/inventory-conversions
 *
 * Owner can Record Conversion: pick a source Purchase Item + qty
 * consumed + one or more target Purchase Items + qty created + a
 * historical conversion_date, plus optional notes and source purchase
 * link.  Explicit conversions override inference (no double counting).
 */
function money(n) { return `$${(Number(n) || 0).toFixed(2)}`; }
function todayISO() { return new Date().toISOString().slice(0, 10); }

export default function AdminInventoryConversions() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/admin/inventory-conversions", { params: { limit: 200 } });
      setRows(r.data.conversions || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed.");
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function cancelRow(id) {
    if (!window.confirm("Cancel this conversion? Its inventory effect will be removed.")) return;
    try {
      await api.post(`/admin/inventory-conversions/${id}/cancel`);
      toast.success("Conversion cancelled.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed.");
    }
  }

  return (
    <div className="min-h-screen bg-[var(--paper,_#fafaf7)]" data-testid="admin-conversions">
      <div className="max-w-5xl mx-auto p-3 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <button onClick={() => (window.location.href = "/admin")}
                    className="text-[10px] uppercase text-[var(--smoke)] flex items-center gap-1">
              <ArrowLeft size={12}/> Admin
            </button>
            <div className="text-2xl font-black flex items-center gap-2">
              <RefreshCcw size={22}/> Inventory Conversions
            </div>
            <div className="text-[11px] text-[var(--smoke)]">
              Historical box openings, bundle fractionations, etc. — explicit records override inference.
            </div>
          </div>
          <button onClick={() => setShowAdd(true)}
                  data-testid="conversion-add-btn"
                  className="bg-[var(--panini-red,_#dc2626)] text-white text-sm font-bold rounded-2xl px-3 py-2 flex items-center gap-1">
            <Plus size={14}/> Record Conversion
          </button>
        </div>

        <div className="space-y-2" data-testid="conversion-rows">
          {loading && <div className="text-sm text-[var(--smoke)]">Loading…</div>}
          {!loading && rows.length === 0 && (
            <div className="text-sm text-[var(--smoke)]" data-testid="conversions-empty">
              No conversions recorded yet.
            </div>
          )}
          {rows.map((c) => (
            <ConversionRow key={c.id} c={c}
                              onEdit={() => setEditing(c)}
                              onCancel={() => cancelRow(c.id)}/>
          ))}
        </div>
      </div>

      {showAdd && <ConversionEditor onClose={() => setShowAdd(false)}
                                        onSaved={() => { setShowAdd(false); load(); }}/>}
      {editing && <ConversionEditor existing={editing}
                                        onClose={() => setEditing(null)}
                                        onSaved={() => { setEditing(null); load(); }}/>}
    </div>
  );
}

function ConversionRow({ c, onEdit, onCancel }) {
  const [sourceName, setSourceName] = useState(c.source_item_id);
  const [targetNames, setTargetNames] = useState({});
  useEffect(() => {
    (async () => {
      try {
        const ids = [c.source_item_id,
                      ...(c.targets || []).map((t) => t.target_item_id)];
        const all = await Promise.all(ids.map((id) =>
             api.get(`/admin/purchase-items/${id}`).catch(() => null)));
        setSourceName((all[0]?.data?.name) || c.source_item_id);
        const map = {};
        (c.targets || []).forEach((t, i) => {
          map[t.target_item_id] = all[i + 1]?.data?.name || t.target_item_id;
        });
        setTargetNames(map);
      } catch { /* ignore */ }
    })();
  }, [c.id, c.source_item_id, c.targets]);

  return (
    <div data-testid={`conversion-row-${c.id}`}
          className="border-2 border-[var(--ink)] rounded-2xl p-3 bg-white text-sm">
      <div className="flex justify-between items-start gap-2">
        <div className="min-w-0">
          <div className="font-bold truncate">
            {sourceName} <span className="text-[var(--smoke)]">× {c.source_quantity_consumed}</span> →
          </div>
          <div className="text-xs">
            {(c.targets || []).length === 0
              ? <i className="text-[var(--smoke)]">partial / unknown composition</i>
              : (c.targets || []).map((t) => (
                  <div key={t.target_item_id}>
                    <b>{targetNames[t.target_item_id] || t.target_item_id}</b> × {t.quantity_created}
                  </div>
                ))}
          </div>
          <div className="text-[11px] text-[var(--smoke)] font-mono">
            {c.public_conversion_id} · {c.conversion_date}
            {c.partial && " · partial"}
            {c.source_purchase_id && ` · ← ${c.source_purchase_id.slice(0, 8)}…`}
          </div>
          {c.notes && <div className="text-xs">{c.notes}</div>}
        </div>
        <div className="text-right">
          <span className={`text-[10px] px-2 py-0.5 rounded-full ${
            c.status === "completed" ? "bg-green-200" : "bg-gray-300"
          }`}>{c.status}</span>
          {c.status === "completed" && (
            <div className="flex gap-1 pt-1">
              <button onClick={onEdit} data-testid={`conversion-edit-${c.id}`}
                      className="text-[11px] border-2 border-[var(--ink)] rounded-full px-2 py-0.5">
                Edit
              </button>
              <button onClick={onCancel} data-testid={`conversion-cancel-${c.id}`}
                      className="text-[11px] border-2 border-red-500 text-red-700 rounded-full px-2 py-0.5">
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ItemPickerLite({ value, onChange, exclude, testid }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [shopify, setShopify] = useState([]);
  const [searched, setSearched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [selName, setSelName] = useState(null);

  useEffect(() => {
    if (value && !selName) {
      api.get(`/admin/purchase-items/${value}`)
          .then((r) => setSelName(r.data?.name)).catch(() => {});
    }
    if (!value) setSelName(null);
  }, [value, selName]);

  useEffect(() => {
    setSearched(false);
    const h = setTimeout(async () => {
      try {
        // Phase 7A.3 fix — reuse the same dual-source search as the
        // Add Purchase picker: internal purchase_items + Shopify mirror.
        const [pi, sp] = await Promise.all([
          api.get("/admin/purchase-items",
                    { params: { q: q || undefined, limit: 15,
                                active: true } }),
          api.get("/admin/shopify-products/search",
                    { params: { q, limit: 25 } })
             .catch(() => ({ data: { results: [] } })),
        ]);
        setItems((pi.data?.purchase_items || [])
                    .filter((it) => it.id !== exclude));
        // Drop Shopify rows already surfaced as a linked purchase_item
        // so the owner never sees the same variant twice.
        const linkedVariants = new Set(
          (pi.data?.purchase_items || [])
            .map((it) => it.shopify_variant_id && String(it.shopify_variant_id))
            .filter(Boolean));
        setShopify((sp.data?.results || [])
                      .filter((s) => !linkedVariants.has(
                          s.shopify_variant_id && String(s.shopify_variant_id))));
      } catch {
        setItems([]); setShopify([]);
      } finally { setSearched(true); }
    }, 180);
    return () => clearTimeout(h);
  }, [q, exclude]);

  async function pickShopify(s) {
    if (!s.shopify_variant_id) {
      toast.error("Shopify item is missing a variant id.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/admin/purchase-items/resolve-shopify", {
        shopify_variant_id: s.shopify_variant_id,
        shopify_product_id: s.shopify_product_id || null,
        name: s.title,
      });
      const it = r.data?.purchase_item;
      if (!it?.id) throw new Error("resolve failed");
      onChange(it.id);
      setSelName(it.name);
      setQ("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not link Shopify item.");
    } finally { setBusy(false); }
  }

  if (value && selName) {
    return (
      <div className="flex justify-between items-center border rounded-lg px-2 py-1 bg-gray-50 text-xs"
            data-testid={testid}>
        <span className="truncate">{selName}</span>
        <button onClick={() => { onChange(null); setSelName(null); }}
                 className="text-red-600 text-[10px] underline">change</button>
      </div>
    );
  }

  const noResults = searched && !busy && items.length === 0 && shopify.length === 0;

  return (
    <div className="relative" data-testid={testid}>
      <input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search item (Shopify mirror + internal)…"
              className="w-full border rounded-lg px-2 py-1 text-xs"/>
      {(items.length > 0 || shopify.length > 0 || noResults) && (
        <div className="absolute z-10 bg-white border rounded-xl mt-1 w-full shadow-lg text-xs max-h-56 overflow-auto"
              data-testid={`${testid}-results`}>
          {items.map((it) => (
            <button key={it.id}
                     onMouseDown={(ev) => ev.preventDefault()}
                     onClick={() => { onChange(it.id); setSelName(it.name); setQ(""); }}
                     data-testid={`${testid}-pick-item-${it.id}`}
                     className="w-full text-left px-2 py-1 hover:bg-gray-50">
              <b>{it.name}</b>
              {it.shopify_variant_id && <span className="text-blue-600"> · Shopify</span>}
            </button>
          ))}
          {shopify.map((s) => (
            <button key={`sp-${s.shopify_variant_id || s.shopify_product_id}`}
                     onMouseDown={(ev) => ev.preventDefault()}
                     onClick={() => pickShopify(s)}
                     disabled={busy}
                     data-testid={`${testid}-pick-shopify-${s.shopify_variant_id || s.shopify_product_id}`}
                     className="w-full text-left px-2 py-1 hover:bg-gray-50 disabled:opacity-50">
              <b>{s.title}</b>
              <span className="text-blue-600"> · Shopify</span>
            </button>
          ))}
          {noResults && (
            <div className="px-2 py-1 text-[var(--smoke)] italic"
                  data-testid={`${testid}-no-results`}>
              No matching items found
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ConversionEditor({ existing, onClose, onSaved }) {
  const isEdit = !!existing;
  const [f, setF] = useState(() => existing ? {
    source_item_id:            existing.source_item_id,
    source_quantity_consumed:  existing.source_quantity_consumed,
    targets:                   (existing.targets || []).map((t) => ({ ...t })),
    conversion_date:           existing.conversion_date || todayISO(),
    source_purchase_id:        existing.source_purchase_id || "",
    notes:                     existing.notes || "",
    partial:                   !!existing.partial,
  } : {
    source_item_id: null, source_quantity_consumed: 1,
    targets: [{ target_item_id: null, quantity_created: 1 }],
    conversion_date: todayISO(),
    source_purchase_id: "", notes: "", partial: false,
  });
  const [busy, setBusy] = useState(false);

  function setTarget(i, patch) {
    setF({ ...f, targets: f.targets.map((t, idx) => idx === i ? { ...t, ...patch } : t) });
  }
  function addTarget() { setF({ ...f, targets: [...f.targets, { target_item_id: null, quantity_created: 1 }] }); }
  function removeTarget(i) { setF({ ...f, targets: f.targets.filter((_, idx) => idx !== i) }); }

  async function save() {
    if (!f.source_item_id) return toast.error("Pick a source item.");
    if (!Number(f.source_quantity_consumed))
      return toast.error("Source quantity must be > 0.");
    const targets = f.targets
      .filter((t) => t.target_item_id && Number(t.quantity_created) > 0)
      .map((t) => ({ target_item_id: t.target_item_id,
                      quantity_created: Number(t.quantity_created) }));
    const body = {
      source_item_id: f.source_item_id,
      source_quantity_consumed: Number(f.source_quantity_consumed),
      targets,
      conversion_date: f.conversion_date,
      source_purchase_id: f.source_purchase_id || null,
      notes: f.notes || null,
      partial: !!f.partial || targets.length === 0,
    };
    setBusy(true);
    try {
      if (isEdit) await api.patch(`/admin/inventory-conversions/${existing.id}`, body);
      else await api.post("/admin/inventory-conversions", body);
      toast.success(isEdit ? "Conversion updated." : "Conversion recorded.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-[100] bg-black/40 flex items-end sm:items-center justify-center sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl border-2 border-[var(--ink)] w-full max-w-lg p-3 space-y-2 max-h-[92vh] overflow-auto"
            data-testid="conversion-editor-modal">
        <div className="flex justify-between">
          <div className="font-bold">{isEdit ? "Edit Conversion" : "Record Conversion"}</div>
          <button onClick={onClose}><X size={20}/></button>
        </div>

        <div>
          <div className="text-[10px] uppercase text-[var(--smoke)]">Conversion date</div>
          <input type="date" value={f.conversion_date}
                  data-testid="conversion-date"
                  onChange={(e) => setF({ ...f, conversion_date: e.target.value })}
                  className="w-full border rounded-xl px-2 py-1.5 text-sm"/>
        </div>

        <div>
          <div className="text-[10px] uppercase text-[var(--smoke)]">Source item *</div>
          <ItemPickerLite value={f.source_item_id}
                             onChange={(id) => setF({ ...f, source_item_id: id })}
                             testid="conversion-source"/>
        </div>
        <div>
          <div className="text-[10px] uppercase text-[var(--smoke)]">Qty consumed *</div>
          <input type="number" min="1" value={f.source_quantity_consumed}
                  data-testid="conversion-source-qty"
                  onChange={(e) => setF({ ...f, source_quantity_consumed: e.target.value })}
                  className="w-full border rounded-xl px-3 py-2 text-sm"/>
        </div>

        <div className="border-t pt-2 space-y-1">
          <div className="flex justify-between">
            <div className="text-[10px] uppercase text-[var(--smoke)]">Targets</div>
            <label className="text-[10px] flex gap-1">
              <input type="checkbox" checked={f.partial}
                      data-testid="conversion-partial"
                      onChange={(e) => setF({ ...f, partial: e.target.checked })}/>
              unknown / partial composition
            </label>
          </div>
          {!f.partial && f.targets.map((t, i) => (
            <div key={i} className="border rounded-lg p-2 space-y-1"
                  data-testid={`conversion-target-${i}`}>
              <div className="flex justify-between items-center">
                <div className="text-[10px] uppercase text-[var(--smoke)]">Target #{i + 1}</div>
                {f.targets.length > 1 && (
                  <button onClick={() => removeTarget(i)} className="text-red-600">
                    <Trash2 size={12}/>
                  </button>
                )}
              </div>
              <ItemPickerLite value={t.target_item_id}
                                 onChange={(id) => setTarget(i, { target_item_id: id })}
                                 exclude={f.source_item_id}
                                 testid={`conversion-target-item-${i}`}/>
              <div>
                <div className="text-[9px] uppercase text-[var(--smoke)]">Quantity created</div>
                <input type="number" min="0" value={t.quantity_created}
                        data-testid={`conversion-target-qty-${i}`}
                        onChange={(e) => setTarget(i, { quantity_created: e.target.value })}
                        className="w-full border rounded-lg px-2 py-1 text-xs"/>
              </div>
            </div>
          ))}
          {!f.partial && (
            <button onClick={addTarget} data-testid="conversion-add-target"
                     className="text-xs border-2 border-[var(--ink)] rounded-full px-2 py-0.5">
              + Add another target
            </button>
          )}
        </div>

        <input value={f.source_purchase_id}
                onChange={(e) => setF({ ...f, source_purchase_id: e.target.value })}
                placeholder="Source purchase id (optional)"
                className="w-full border rounded-xl px-3 py-2 text-xs"/>
        <textarea value={f.notes}
                    onChange={(e) => setF({ ...f, notes: e.target.value })}
                    placeholder="Notes (optional)"
                    className="w-full border rounded-xl px-3 py-2 text-sm h-14"/>

        <button onClick={save} disabled={busy}
                 data-testid="conversion-save-btn"
                 className={`w-full rounded-2xl py-2 font-bold text-white ${
                   busy ? "bg-gray-400" : "bg-[var(--panini-red,_#dc2626)]"
                 }`}>
          {busy ? "Saving…" : isEdit ? "Save Changes" : "Record Conversion"}
        </button>
      </div>
    </div>
  );
}
